import type { Dict } from '../../i18n';

const dict: Dict = {
  en: {
    // Topbar
    subtitleAddressBook: 'My address book',
    subtitleClinic_one: '{{count}} clinic · ordered by activity',
    subtitleClinic_other: '{{count}} clinics · ordered by activity',
    heading: 'My clients',
    searchPlaceholder: 'Search by name, CIF, email…',
    importingBtn: 'Importing…',
    importCsvBtn: 'Import CSV',
    newClientBtn: '+ New client',

    // Table headers
    colClient: 'Client',
    colCity: 'City',
    colCountry: 'Country',
    colEmail: 'Email',
    colCifVat: 'ID / NIF',
    colVat: 'VAT',
    colOrders: 'Orders',
    colLast: 'Last',
    colLifetime: 'Lifetime',

    // VAT validity (dot tooltip)
    vatValid: 'VAT valid',
    vatInvalid: 'VAT invalid',
    vatUnavailable: 'VIES unavailable',
    vatUnverified: 'Not validated yet',
    vatNotApplicable: 'Non-EU — not checked',
    vatCheckedOn: 'checked {{date}}',

    // Row actions
    newOrderBtn: '+ Order',
    editBtn: 'Edit',
    requestRemovalBtn: 'Request removal',
    requestRemovalPrompt: 'Why should "{{name}}" be removed? A super-admin will review the request.',
    requestRemovalDone: 'Removal of "{{name}}" requested — a super-admin will review it.',
    deleteBtn: 'Delete',
    deleteConfirm: 'Delete client "{{name}}"? This cannot be undone.',
    deleteArchivedNotice:
      '"{{name}}" has orders, so it was archived instead of deleted.',

    // Empty / loading / error states
    loadingClients: 'Loading clients…',
    emptyTitle: 'No clients yet',
    emptyHint: 'Add your first clinic with "+ New client".',
    noMatchTitle: 'No matching clients',
    noMatchHint: 'Try a different name, CIF or email.',

    // Tips panel
    tipsHeading: 'Tips',
    tipsCopy:
      'Clients are personal to you. Admins see the global directory. Importing a CSV maps to: name · CIF · email · phone · billing addr · shipping addr.',

    // Missing data panel
    missingDataHeading: 'Missing data',
    missingCif_one: '{{count}} client missing CIF',
    missingCif_other: '{{count}} clients missing CIF',
    allCifOk: 'All clients have a CIF / VAT on file.',
    needsCorrection_one: '{{count}} client needs a correction (blocked for orders)',
    needsCorrection_other: '{{count}} clients need a correction (blocked for orders)',
    allClientsOk: 'All clients are complete and can order.',
    colStatus: 'Status',
    statusBlocked: 'blocked',
    statusOk: 'ok',

    // New client dialog
    dialogTitle: 'New client',
    closeAriaLabel: 'Close',
    fieldCompany: 'Company name',
    fieldCompanyPlaceholder: 'Clínica Sole',
    fieldCifVat: 'CIF / VAT / DNI number',
    fieldEmail: 'Email',
    fieldEmailPlaceholder: 'info@clinicasole.es',
    fieldPhone: 'Phone number',
    fieldPhonePlaceholder: '+34 600 000 000',
    contactPersonHeading: 'Contact person',
    fieldFirstName: 'First name',
    fieldLastName: 'Last name',
    fieldClinicDoctorName: 'Clinic / doctor name',
    fieldCollegiateNumber: 'Collegiate number',
    billingAddressHeading: 'Billing address',
    fieldAddress: 'Address',
    fieldAddress2: 'Address 2',
    fieldCity: 'City',
    fieldProvince: 'State / Province / Region',
    fieldPostalCode: 'ZIP / Postal code',
    fieldCountry: 'Country',
    deliveryAddressHeading: 'Delivery address',
    deliverySameAsBilling: 'Delivery address is the same as billing',
    shippingNote: 'Shipping defaults to the billing address.',
    cancelBtn: 'Cancel',
    savingBtn: 'Saving…',
    createClientBtn: 'Create client',
  },
  es: {
    // Topbar
    subtitleAddressBook: 'Mi agenda',
    subtitleClinic_one: '{{count}} clínica · ordenado por actividad',
    subtitleClinic_other: '{{count}} clínicas · ordenado por actividad',
    heading: 'Mis clientes',
    searchPlaceholder: 'Buscar por nombre, CIF, correo…',
    importingBtn: 'Importando…',
    importCsvBtn: 'Importar CSV',
    newClientBtn: '+ Nuevo cliente',

    // Table headers
    colClient: 'Cliente',
    colCity: 'Ciudad',
    colCountry: 'País',
    colEmail: 'Correo electrónico',
    colCifVat: 'ID / NIF',
    colVat: 'IVA',
    colOrders: 'Pedidos',
    colLast: 'Último',
    colLifetime: 'Total',

    // Validez del IVA (tooltip del punto)
    vatValid: 'IVA válido',
    vatInvalid: 'IVA no válido',
    vatUnavailable: 'VIES no disponible',
    vatUnverified: 'Aún sin validar',
    vatNotApplicable: 'No UE — sin comprobar',
    vatCheckedOn: 'comprobado {{date}}',

    // Row actions
    newOrderBtn: '+ Pedido',
    editBtn: 'Editar',
    requestRemovalBtn: 'Solicitar baja',
    requestRemovalPrompt: '¿Por qué debe darse de baja "{{name}}"? Un superadministrador revisará la solicitud.',
    requestRemovalDone: 'Baja de "{{name}}" solicitada — un superadministrador la revisará.',
    deleteBtn: 'Eliminar',
    deleteConfirm: '¿Eliminar el cliente "{{name}}"? Esta acción no se puede deshacer.',
    deleteArchivedNotice:
      '"{{name}}" tiene pedidos, por lo que se archivó en lugar de eliminarse.',

    // Empty / loading / error states
    loadingClients: 'Cargando clientes…',
    emptyTitle: 'Sin clientes aún',
    emptyHint: 'Añade tu primera clínica con "+ Nuevo cliente".',
    noMatchTitle: 'No se encontró nada',
    noMatchHint: 'Prueba con otro nombre, CIF o correo.',

    // Tips panel
    tipsHeading: 'Consejos',
    tipsCopy:
      'Los clientes son personales. Los administradores ven el directorio global. Al importar un CSV se asignan: nombre · CIF · correo · teléfono · dir. facturación · dir. envío.',

    // Missing data panel
    missingDataHeading: 'Datos incompletos',
    missingCif_one: '{{count}} cliente sin CIF',
    missingCif_other: '{{count}} clientes sin CIF',
    allCifOk: 'Todos los clientes tienen CIF / NIF registrado.',
    needsCorrection_one: '{{count}} cliente necesita corrección (bloqueado para pedidos)',
    needsCorrection_other: '{{count}} clientes necesitan corrección (bloqueados para pedidos)',
    allClientsOk: 'Todos los clientes están completos y pueden pedir.',
    colStatus: 'Estado',
    statusBlocked: 'bloqueado',
    statusOk: 'ok',

    // New client dialog
    dialogTitle: 'Nuevo cliente',
    closeAriaLabel: 'Cerrar',
    fieldCompany: 'Nombre de la empresa',
    fieldCompanyPlaceholder: 'Clínica Sole',
    fieldCifVat: 'CIF / VAT / DNI número',
    fieldEmail: 'Correo electrónico',
    fieldEmailPlaceholder: 'info@clinicasole.es',
    fieldPhone: 'Número de teléfono',
    fieldPhonePlaceholder: '+34 600 000 000',
    contactPersonHeading: 'Persona de contacto',
    fieldFirstName: 'Nombre',
    fieldLastName: 'Apellido',
    fieldClinicDoctorName: 'Nombre de la clínica / doctor',
    fieldCollegiateNumber: 'Número de colegiado',
    billingAddressHeading: 'Dirección de facturación',
    fieldAddress: 'Dirección',
    fieldAddress2: 'Dirección 2',
    fieldCity: 'Ciudad',
    fieldProvince: 'Estado / Provincia / Región',
    fieldPostalCode: 'ZIP / Código Postal',
    fieldCountry: 'País',
    deliveryAddressHeading: 'Dirección de entrega',
    deliverySameAsBilling: 'La dirección de entrega es la misma que la de facturación',
    shippingNote: 'El envío usa la dirección de facturación por defecto.',
    cancelBtn: 'Cancelar',
    savingBtn: 'Guardando…',
    createClientBtn: 'Crear cliente',
  },
  de: {
    // Topbar
    subtitleAddressBook: 'Mein Adressbuch',
    subtitleClinic_one: '{{count}} Klinik · nach Aktivität sortiert',
    subtitleClinic_other: '{{count}} Kliniken · nach Aktivität sortiert',
    heading: 'Meine Kunden',
    searchPlaceholder: 'Nach Name, CIF, E-Mail suchen…',
    importingBtn: 'Wird importiert…',
    importCsvBtn: 'CSV importieren',
    newClientBtn: '+ Neuer Kunde',

    // Table headers
    colClient: 'Kunde',
    colCity: 'Stadt',
    colCountry: 'Land',
    colEmail: 'E-Mail',
    colCifVat: 'ID / NIF',
    colVat: 'MwSt.',
    colOrders: 'Bestellungen',
    colLast: 'Zuletzt',
    colLifetime: 'Gesamt',

    // VAT validity (dot tooltip)
    vatValid: 'USt-IdNr. gültig',
    vatInvalid: 'USt-IdNr. ungültig',
    vatUnavailable: 'VIES nicht verfügbar',
    vatUnverified: 'Noch nicht geprüft',
    vatNotApplicable: 'Nicht-EU — nicht geprüft',
    vatCheckedOn: 'geprüft am {{date}}',

    // Row actions
    newOrderBtn: '+ Bestellung',
    editBtn: 'Bearbeiten',
    requestRemovalBtn: 'Entfernung anfragen',
    requestRemovalPrompt: 'Warum soll „{{name}}“ entfernt werden? Ein Super-Admin prüft die Anfrage.',
    requestRemovalDone: 'Entfernung von „{{name}}“ angefragt — ein Super-Admin prüft sie.',
    deleteBtn: 'Löschen',
    deleteConfirm: 'Kunde „{{name}}“ löschen? Dies kann nicht rückgängig gemacht werden.',
    deleteArchivedNotice:
      '„{{name}}“ hat Bestellungen und wurde daher archiviert statt gelöscht.',

    // Empty / loading / error states
    loadingClients: 'Kunden werden geladen…',
    emptyTitle: 'Noch keine Kunden',
    emptyHint: 'Fügen Sie Ihre erste Klinik mit „+ Neuer Kunde“ hinzu.',
    noMatchTitle: 'Keine passenden Kunden',
    noMatchHint: 'Versuchen Sie einen anderen Namen, CIF oder eine andere E-Mail.',

    // Tips panel
    tipsHeading: 'Tipps',
    tipsCopy:
      'Kunden sind Ihnen persönlich zugeordnet. Administratoren sehen das globale Verzeichnis. Ein CSV-Import ordnet zu: Name · CIF · E-Mail · Telefon · Rechnungsadr. · Lieferadr.',

    // Missing data panel
    missingDataHeading: 'Fehlende Daten',
    missingCif_one: '{{count}} Kunde ohne CIF',
    missingCif_other: '{{count}} Kunden ohne CIF',
    allCifOk: 'Alle Kunden haben einen CIF / eine USt-IdNr. hinterlegt.',
    needsCorrection_one: '{{count}} Kunde braucht eine Korrektur (für Bestellungen gesperrt)',
    needsCorrection_other: '{{count}} Kunden brauchen eine Korrektur (für Bestellungen gesperrt)',
    allClientsOk: 'Alle Kunden sind vollständig und können bestellen.',
    colStatus: 'Status',
    statusBlocked: 'gesperrt',
    statusOk: 'ok',

    // New client dialog
    dialogTitle: 'Neuer Kunde',
    closeAriaLabel: 'Schließen',
    fieldCompany: 'Firmenname',
    fieldCompanyPlaceholder: 'Clínica Sole',
    fieldCifVat: 'CIF / USt-IdNr. / DNI-Nummer',
    fieldEmail: 'E-Mail',
    fieldEmailPlaceholder: 'info@clinicasole.es',
    fieldPhone: 'Telefonnummer',
    fieldPhonePlaceholder: '+34 600 000 000',
    contactPersonHeading: 'Ansprechpartner',
    fieldFirstName: 'Vorname',
    fieldLastName: 'Nachname',
    fieldClinicDoctorName: 'Klinik-/Arztname',
    fieldCollegiateNumber: 'Kammernummer',
    billingAddressHeading: 'Rechnungsadresse',
    fieldAddress: 'Adresse',
    fieldAddress2: 'Adresse 2',
    fieldCity: 'Stadt',
    fieldProvince: 'Bundesland/Provinz/Region',
    fieldPostalCode: 'PLZ / Postleitzahl',
    fieldCountry: 'Land',
    deliveryAddressHeading: 'Lieferadresse',
    deliverySameAsBilling: 'Die Lieferadresse entspricht der Rechnungsadresse',
    shippingNote: 'Der Versand erfolgt standardmäßig an die Rechnungsadresse.',
    cancelBtn: 'Abbrechen',
    savingBtn: 'Speichert…',
    createClientBtn: 'Kunde anlegen',
  },
};

export default dict;
